import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, Crown, FileDown, FileSignature } from 'lucide-react';
import { jsPDF } from 'jspdf';
import { useAuth } from '../context/AuthContext';
import {
  CLIENT_FIELDS, SCREENING_INTRO, SCREENING, EXPLAIN_FIELDS, SECTIONS,
  TOUCH_CONSENT, AFTER_TOUCH_SECTIONS, ACKNOWLEDGEMENT, GUARDIAN_TEXT, WAIVER_LABELS,
} from '../data/liabilityWaiver';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const todayIso = () => new Date().toISOString().slice(0, 10);
const slug = (s) => s.replace(/[^a-z0-9]+/gi, '-').toLowerCase();

const LegalSection = ({ sec }) => (
  <section className="mb-6" data-testid={`waiver-section-${sec.num}`}>
    <h3 className="font-heading text-lg text-forest mb-2">{sec.num}. {sec.title}</h3>
    {sec.body.map((p, i) => (
      <p key={i} className="font-body text-xs text-stone-600 leading-relaxed mb-2">{p}</p>
    ))}
    {sec.list && (
      <ul className="list-disc pl-5 mb-2 space-y-1">
        {sec.list.map((li, i) => <li key={i} className="font-body text-xs text-stone-600">{li}</li>)}
      </ul>
    )}
    {sec.after && sec.after.map((p, i) => (
      <p key={i} className="font-body text-xs text-stone-600 leading-relaxed mb-2">{p}</p>
    ))}
  </section>
);

const LiabilityWaiverPage = () => {
  const { user, hasAccess, loading, refreshUser } = useAuth();
  const [answers, setAnswers] = useState({});
  const [touch, setTouch] = useState('');
  const [ackChecked, setAckChecked] = useState(false);
  const [signedName, setSignedName] = useState('');
  const [signedDate, setSignedDate] = useState(todayIso());
  const [practitioner, setPractitioner] = useState('');
  const [practitionerDate, setPractitionerDate] = useState('');
  const [guardianName, setGuardianName] = useState('');
  const [guardianDate, setGuardianDate] = useState('');
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [emailCopy, setEmailCopy] = useState(false);
  const [emailStatus, setEmailStatus] = useState('');

  useEffect(() => { if (user?.id) refreshUser(user.id); /* eslint-disable-next-line */ }, []);

  const load = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await fetch(`${API}/liability-waiver/${user.id}`);
      const d = await res.json();
      if (d.exists) {
        setAnswers(d.answers || {});
        setTouch(d.touch_consent || '');
        setAckChecked(!!d.acknowledged);
        setSignedName(d.signed_name || '');
        setSignedDate(d.signed_date || todayIso());
        setPractitioner(d.practitioner_name || '');
        setPractitionerDate(d.practitioner_date || '');
        setGuardianName(d.guardian_name || '');
        setGuardianDate(d.guardian_date || '');
      } else {
        setAnswers((a) => ({ ...a, name: user.name || '', email: user.email || '' }));
      }
    } catch (e) { console.error(e); }
  }, [user]);
  useEffect(() => { load(); }, [load]);

  const set = (key, val) => { setSaved(false); setAnswers((a) => ({ ...a, [key]: val })); };

  const save = async () => {
    setSaving(true);
    setEmailStatus('');
    try {
      const res = await fetch(`${API}/liability-waiver`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id, user_email: user.email, answers,
          touch_consent: touch, acknowledged: ackChecked,
          signed_name: signedName, signed_date: signedDate,
          practitioner_name: practitioner, practitioner_date: practitionerDate,
          guardian_name: guardianName, guardian_date: guardianDate,
        }),
      });
      if (res.ok) {
        setSaved(true);
        if (emailCopy) {
          setEmailStatus('sending');
          try {
            const b64 = buildDoc().output('datauristring').split('base64,')[1];
            const er = await fetch(`${API}/liability-waiver/email-copy`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ user_email: user.email, client_name: signedName, pdf_base64: b64 }),
            });
            const ed = await er.json();
            setEmailStatus(ed.sent ? 'sent' : 'failed');
          } catch (e) { console.error(e); setEmailStatus('failed'); }
        }
      }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const buildDoc = () => {
    const doc = new jsPDF({ unit: 'pt', format: 'letter' });
    const W = doc.internal.pageSize.getWidth(), H = doc.internal.pageSize.getHeight(), m = 46;
    let y = m;
    const ln = (t, x, s = 10, b = false, c = [60, 60, 60]) => {
      doc.setFont('helvetica', b ? 'bold' : 'normal'); doc.setFontSize(s); doc.setTextColor(...c);
      doc.splitTextToSize(t, W - m - x).forEach((w) => { if (y > H - m) { doc.addPage(); y = m; } doc.text(w, x, y); y += s + 3; });
    };
    const sec = (title) => { if (y > H - m - 24) { doc.addPage(); y = m; } y += 4; ln(title, m, 12, true, [44, 62, 46]); };

    ln('Grounded Wellness with Jill', m, 17, true, [44, 62, 46]);
    ln('Client Health Screening, Informed Consent & Liability Waiver', m, 11, false, [140, 110, 90]); y += 4;

    sec('Client Information');
    CLIENT_FIELDS.forEach((f) => ln(`${f.label}: ${answers[f.key] || '—'}`, m + 8, 10));

    sec('1. Health & Physical Activity Screening');
    SCREENING.forEach((g) => {
      ln(g.group, m + 8, 10, true, [138, 106, 90]);
      g.items.forEach((it) => ln(`${WAIVER_LABELS[it.key]}  →  ${answers[it.key] || '—'}`, m + 12, 9));
    });
    EXPLAIN_FIELDS.forEach((f) => { if (answers[f.key]) { ln(f.label, m + 8, 10, true); ln(answers[f.key], m + 12, 9); } });

    [...SECTIONS].forEach((s) => {
      sec(`${s.num}. ${s.title}`);
      s.body.forEach((p) => ln(p, m + 8, 9));
      (s.list || []).forEach((li) => ln(`• ${li}`, m + 12, 9));
      (s.after || []).forEach((p) => ln(p, m + 8, 9));
    });

    sec(`${TOUCH_CONSENT.num}. ${TOUCH_CONSENT.title}`);
    TOUCH_CONSENT.body.forEach((p) => ln(p, m + 8, 9));
    ln(`${TOUCH_CONSENT.question}  →  ${touch || '—'}`, m + 8, 10, true);

    AFTER_TOUCH_SECTIONS.forEach((s) => { sec(`${s.num}. ${s.title}`); s.body.forEach((p) => ln(p, m + 8, 9)); });

    sec(`${ACKNOWLEDGEMENT.num}. ${ACKNOWLEDGEMENT.title}`);
    ln(`${ackChecked ? '[x]' : '[ ]'} ${ACKNOWLEDGEMENT.intro}`, m + 8, 10, true);
    ACKNOWLEDGEMENT.items.forEach((it) => ln(`• ${it}`, m + 12, 9));
    y += 6;
    ln(`Client Name: ${signedName || '—'}`, m + 8, 10);
    ln(`Client Signature: ${signedName || '—'}    Date: ${signedDate || '—'}`, m + 8, 10);
    ln(`Practitioner / Witness: ${practitioner || '—'}    Date: ${practitionerDate || '—'}`, m + 8, 10);
    if (guardianName) {
      y += 4;
      ln('For participants under 18', m + 8, 10, true, [138, 106, 90]);
      ln(GUARDIAN_TEXT, m + 8, 9);
      ln(`Parent / Guardian: ${guardianName}    Date: ${guardianDate || '—'}`, m + 8, 10);
    }
    return doc;
  };

  const downloadPdf = () => buildDoc().save('liability-waiver.pdf');

  if (loading) return <div className="min-h-screen flex items-center justify-center text-stone-500 font-body">Loading…</div>;

  if (!user || !hasAccess('vip')) {
    return (
      <div className="min-h-screen bg-cream py-16 px-4" data-testid="waiver-access-gate">
        <div className="max-w-xl mx-auto text-center bg-[#f5efe4] border border-stone-200 rounded-2xl p-10">
          <Crown size={26} className="text-forest mx-auto mb-4" />
          <h1 className="font-heading text-2xl text-forest mb-2">Liability Waiver</h1>
          <p className="font-body text-stone-600 text-sm mb-6">Available to Jill's V.I.P. members & package holders.</p>
          <Link to="/programs" className="inline-flex items-center gap-2 bg-forest text-cream font-body px-6 py-3 rounded-full">Back to Programs</Link>
        </div>
      </div>
    );
  }

  const yn = (item) => {
    const opts = item.options || ['Yes', 'No'];
    return (
      <div className="flex flex-wrap gap-2 mt-2" data-testid={`waiver-field-${item.key}`}>
        {opts.map((o) => (
          <button key={o} type="button" onClick={() => set(item.key, o)}
            className={`px-4 py-1.5 rounded-full font-body text-xs border transition-colors ${answers[item.key] === o ? 'bg-forest text-cream border-forest' : 'bg-white text-stone-600 border-stone-200 hover:border-forest/40'}`}
            data-testid={`waiver-radio-${item.key}-${slug(o)}`}>
            {o}
          </button>
        ))}
      </div>
    );
  };

  const sigInput = (val, onChange, testid) => (
    <input value={val} onChange={(e) => { setSaved(false); onChange(e.target.value); }}
      className="w-full font-body text-sm bg-white border border-stone-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-forest"
      data-testid={testid} />
  );
  const dateInput = (val, onChange, testid) => (
    <input type="date" value={val} onChange={(e) => { setSaved(false); onChange(e.target.value); }}
      className="font-body text-sm bg-white border border-stone-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-forest"
      data-testid={testid} />
  );

  return (
    <div className="min-h-screen bg-cream py-10 px-4" data-testid="liability-waiver-page">
      <div className="max-w-2xl mx-auto">
        <Link to="/programs/vip/start-here" className="inline-flex items-center gap-2 text-forest/70 hover:text-forest font-body text-sm mb-6">
          <ArrowLeft size={16} /> Back to Start Here
        </Link>

        <div className="flex items-center gap-2 mb-2">
          <FileSignature size={16} className="text-[#8a6a5a]" />
          <p className="text-xs font-body text-[#8a6a5a] uppercase tracking-[0.25em]">Liability Waiver</p>
        </div>
        <h1 className="font-heading text-3xl md:text-4xl text-forest mb-1">Client Health Screening, Informed Consent &amp; Liability Waiver</h1>
        <p className="font-body text-xs text-stone-500 mb-6 max-w-xl">
          Please review and complete before your first session. Your answers save to your profile and you can download a copy.
        </p>

        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
          {/* Client information */}
          <div className="bg-white border border-stone-200 rounded-2xl p-6" data-testid="waiver-client-info">
            <h2 className="font-heading text-xl text-forest mb-4">Client Information</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {CLIENT_FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="block text-[11px] font-body uppercase tracking-wider text-stone-500 mb-1">{f.label}</label>
                  {sigInput(answers[f.key] || '', (v) => set(f.key, v), `waiver-field-${f.key}`)}
                </div>
              ))}
            </div>
          </div>

          {/* Screening */}
          <div className="bg-white border border-stone-200 rounded-2xl p-6" data-testid="waiver-screening">
            <h2 className="font-heading text-xl text-forest mb-1">1. Health &amp; Physical Activity Screening</h2>
            <p className="font-body text-xs text-stone-500 italic mb-4">{SCREENING_INTRO}</p>
            {SCREENING.map((g) => (
              <div key={g.group} className="mb-5">
                <p className="font-body text-sm font-semibold text-[#8a6a5a] mb-3">{g.group}</p>
                <div className="space-y-4">
                  {g.items.map((it, i) => (
                    <div key={it.key}>
                      <label className="block font-body text-sm text-stone-700 leading-snug">
                        <span className="text-stone-400 mr-1">{SCREENING.slice(0, SCREENING.indexOf(g)).reduce((n, gr) => n + gr.items.length, 0) + i + 1}.</span>
                        {it.label}
                      </label>
                      {yn(it)}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div className="space-y-4 pt-2 border-t border-stone-100">
              {EXPLAIN_FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="block font-body text-sm text-stone-700 mb-1.5">{f.label}</label>
                  <textarea rows={2} value={answers[f.key] || ''} onChange={(e) => set(f.key, e.target.value)}
                    className="w-full font-body text-sm bg-white border border-stone-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-forest"
                    data-testid={`waiver-field-${f.key}`} />
                </div>
              ))}
            </div>
          </div>

          {/* Legal sections 2–8 */}
          <div className="bg-[#f8f4ec] border border-stone-200 rounded-2xl p-6" data-testid="waiver-legal">
            {SECTIONS.map((s) => <LegalSection key={s.num} sec={s} />)}

            {/* Touch consent */}
            <section className="mb-6" data-testid={`waiver-section-${TOUCH_CONSENT.num}`}>
              <h3 className="font-heading text-lg text-forest mb-2">{TOUCH_CONSENT.num}. {TOUCH_CONSENT.title}</h3>
              {TOUCH_CONSENT.body.map((p, i) => (
                <p key={i} className="font-body text-xs text-stone-600 leading-relaxed mb-2">{p}</p>
              ))}
              <label className="block font-body text-sm text-stone-700 font-semibold mt-3 mb-1">{TOUCH_CONSENT.question}</label>
              <div className="flex flex-wrap gap-2 mt-1" data-testid="waiver-field-touch">
                {TOUCH_CONSENT.options.map((o) => (
                  <button key={o} type="button" onClick={() => { setSaved(false); setTouch(o); }}
                    className={`px-4 py-1.5 rounded-full font-body text-xs border transition-colors ${touch === o ? 'bg-forest text-cream border-forest' : 'bg-white text-stone-600 border-stone-200 hover:border-forest/40'}`}
                    data-testid={`waiver-touch-${slug(o)}`}>
                    {o}
                  </button>
                ))}
              </div>
            </section>

            {AFTER_TOUCH_SECTIONS.map((s) => <LegalSection key={s.num} sec={s} />)}
          </div>

          {/* Acknowledgement + signatures */}
          <div className="bg-white border border-stone-200 rounded-2xl p-6" data-testid="waiver-acknowledgement">
            <h2 className="font-heading text-xl text-forest mb-2">{ACKNOWLEDGEMENT.num}. {ACKNOWLEDGEMENT.title}</h2>
            <p className="font-body text-sm text-stone-700 mb-3">{ACKNOWLEDGEMENT.intro}</p>
            <ul className="space-y-2 mb-4">
              {ACKNOWLEDGEMENT.items.map((it, i) => (
                <li key={i} className="font-body text-xs text-stone-600 leading-relaxed flex gap-2">
                  <Check size={14} className="text-forest flex-shrink-0 mt-0.5" /> {it}
                </li>
              ))}
            </ul>
            <label className="flex items-start gap-2 cursor-pointer mb-5" data-testid="waiver-ack-checkbox">
              <input type="checkbox" checked={ackChecked} onChange={(e) => { setSaved(false); setAckChecked(e.target.checked); }}
                className="mt-1 accent-[#2c3e2e]" />
              <span className="font-body text-sm text-stone-700">I have read, understood and agree to all of the above.</span>
            </label>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-body uppercase tracking-wider text-stone-500 mb-1">Client name / signature (type your name)</label>
                {sigInput(signedName, setSignedName, 'waiver-signed-name')}
              </div>
              <div>
                <label className="block text-[11px] font-body uppercase tracking-wider text-stone-500 mb-1">Date</label>
                {dateInput(signedDate, setSignedDate, 'waiver-signed-date')}
              </div>
              <div>
                <label className="block text-[11px] font-body uppercase tracking-wider text-stone-500 mb-1">Practitioner / Witness</label>
                {sigInput(practitioner, setPractitioner, 'waiver-practitioner-name')}
              </div>
              <div>
                <label className="block text-[11px] font-body uppercase tracking-wider text-stone-500 mb-1">Date</label>
                {dateInput(practitionerDate, setPractitionerDate, 'waiver-practitioner-date')}
              </div>
            </div>
          </div>

          {/* Guardian */}
          <div className="bg-white border border-stone-200 rounded-2xl p-6" data-testid="waiver-guardian">
            <h2 className="font-heading text-lg text-forest mb-2">For participants under 18</h2>
            <p className="font-body text-xs text-stone-600 leading-relaxed mb-4">{GUARDIAN_TEXT}</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-body uppercase tracking-wider text-stone-500 mb-1">Parent / Guardian name / signature</label>
                {sigInput(guardianName, setGuardianName, 'waiver-guardian-name')}
              </div>
              <div>
                <label className="block text-[11px] font-body uppercase tracking-wider text-stone-500 mb-1">Date</label>
                {dateInput(guardianDate, setGuardianDate, 'waiver-guardian-date')}
              </div>
            </div>
          </div>

          <label className="flex items-start gap-2 cursor-pointer" data-testid="waiver-email-copy-toggle">
            <input type="checkbox" checked={emailCopy} onChange={(e) => setEmailCopy(e.target.checked)} className="mt-1 accent-[#2c3e2e]" />
            <span className="font-body text-sm text-stone-700">Email me a PDF copy when I save</span>
          </label>

          {saved && (
            <div className="bg-forest/10 border border-forest/20 rounded-lg px-4 py-3 font-body text-sm text-forest" data-testid="waiver-saved-msg">
              ✓ Saved to your profile. You can download a copy below.
              {emailStatus === 'sending' && <span className="block text-xs text-stone-500 mt-1">Emailing your copy…</span>}
              {emailStatus === 'sent' && <span className="block text-xs mt-1" data-testid="waiver-email-sent">✓ A copy was emailed to {user.email}.</span>}
              {emailStatus === 'failed' && <span className="block text-xs text-[#8a6a5a] mt-1" data-testid="waiver-email-failed">Saved — but we couldn't email the copy just now. You can download it below.</span>}
            </div>
          )}

          <div className="flex flex-wrap gap-3 pb-6">
            <button onClick={save} disabled={!signedName || !ackChecked || saving}
              className="bg-forest text-cream font-body text-sm font-medium px-6 py-2.5 rounded-full hover:bg-forest-dark transition-colors disabled:opacity-50" data-testid="waiver-submit">
              {saving ? 'Saving…' : saved ? 'Save again' : 'Submit & save'}
            </button>
            <button onClick={downloadPdf} className="inline-flex items-center gap-1.5 bg-[#8a6a5a] text-cream font-body text-sm px-5 py-2.5 rounded-full hover:bg-[#755848] transition-colors" data-testid="waiver-download">
              <FileDown size={14} /> Download PDF
            </button>
          </div>
          {(!signedName || !ackChecked) && (
            <p className="font-body text-[11px] text-stone-400 -mt-3 pb-6">Tick the acknowledgement and type your name to enable saving.</p>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default LiabilityWaiverPage;
